import React from 'react';
import { Link } from 'react-router-dom';
import { IconArrowRight, IconArrowLeft } from './Icons';
import './Button.css';

/**
 * Reusable Button Component
 * 
 * Usage:
 * <Button>Click me</Button>
 * <Button to="/portfolio">Go to Portfolio</Button>
 * <Button href="https://calendly.com">Book Call</Button>
 * <Button variant="solid">Solid Button</Button>
 * <Button size="small">Small Button</Button>
 * 
 * Props:
 * - variant: 'outline' (default) | 'solid' | 'ghost'
 * - size: 'medium' (default) | 'small' | 'large'
 * - to: internal link (uses React Router)
 * - href: external link (uses <a> tag)
 * - icon: 'arrow-right' (default) | 'arrow-left' | null
 * - iconPosition: 'right' (default) | 'left'
 * - className: additional classes
 * - onClick: click handler
 */

const Button = ({ 
  children, 
  variant = 'outline', 
  size = 'medium',
  to,
  href,
  icon = 'arrow-right',
  iconPosition = 'right',
  className = '',
  onClick,
  ...props 
}) => {
  const classes = `btn-fox btn-fox--${variant} btn-fox--${size} ${className}`.trim();
  
  const getIcon = () => {
    if (!icon) return null;
    switch(icon) {
      case 'arrow-right':
        return <IconArrowRight size={14} />;
      case 'arrow-left':
        return <IconArrowLeft size={14} />;
      default:
        return <IconArrowRight size={14} />;
    }
  };
  
  const content = (
    <>
      {iconPosition === 'left' && icon && <span className="btn-fox__icon">{getIcon()}</span>}
      <span>{children}</span>
      {iconPosition === 'right' && icon && <span className="btn-fox__icon">{getIcon()}</span>}
    </>
  );

  // Internal link
  if (to) {
    return (
      <Link to={to} className={classes} {...props}>
        {content}
      </Link>
    );
  }

  // External link
  if (href) {
    return (
      <a 
        href={href} 
        className={classes} 
        target="_blank" 
        rel="noopener noreferrer"
        {...props}
      >
        {content}
      </a>
    );
  }

  // Regular button
  return (
    <button className={classes} onClick={onClick} {...props}>
      {content}
    </button>
  );
};

export default Button;
